import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Input, Pagination } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import PageTitle from '@components/PageTitle'
import Spinner from '@components/Spinner'
import Status from '@components/Status'
import Table from '@components/Table'
import Td from '@components/Td'
import Template from '@components/Template'
import Tr from '@components/Tr'
import { legalDocumentApi } from '@services/LegalDocumentApi'
import { useQuery } from '@utils/hooks'

const styles = {
	header: 'gap-[8px] mb-[32px] items-end flex-wrap',
	divider: 'mt-[32px] mb-[12px]',
	search: 'w-[320px]',
	slug: 'block text-sm text-gray',
}

// Abaixo disto a norma quase certamente entrou pela metade: um código, uma
// constituição ou uma lei ordinária têm dezenas de artigos. Súmula e tema
// avulsos têm um só, e por isso o alerta olha o tipo antes de acender.
const SUSPICIOUSLY_FEW_PROVISIONS = 5
const SINGLE_PROVISION_KINDS = ['sumula', 'sumula-vinculante', 'tema']

function LegalDocuments() {
	const pages = [{ name: 'Acervo de documentos', href: '#', current: true }]
	const navigate = useNavigate()
	const query = useQuery()

	const pageQuery = Number(query.get('page')) || 1
	const [page, setPage] = useState(pageQuery)
	const [search, setSearch] = useState('')
	const [applied, setApplied] = useState('')

	const { data, isLoading, isFetching } = legalDocumentApi.useListQuery({
		page,
		search: applied || undefined,
	})

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	const formatDate = (value: string) =>
		new Date(value).toLocaleString('pt-BR', {
			dateStyle: 'short',
			timeStyle: 'short',
		})

	const looksTruncated = (provisionCount: number, kind: string) =>
		provisionCount < SUSPICIOUSLY_FEW_PROVISIONS &&
		!SINGLE_PROVISION_KINDS.includes(kind)

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<PageTitle title='Acervo de documentos' />

			<p className='mt-2 mb-8 text-gray max-w-[820px] leading-relaxed'>
				As normas que a conversão usa para fundamentar as questões. Cada uma
				entra inteira, conferida contra o documento oficial. Enquanto a norma
				não está aqui, a conversão precisa buscar os artigos na internet, e o
				que ela traz são recortes do tamanho da questão, sem como conferir se a
				transcrição está correta.
			</p>

			<Flex className={styles.header}>
				<form
					className={styles.search}
					onSubmit={(event) => {
						event.preventDefault()
						setApplied(search)
						setPage(1)
					}}
				>
					<Input
						type='text'
						placeholder='Buscar por nome ou endereço'
						value={search}
						onChange={(event) => setSearch(event.target.value)}
					/>
				</form>

				<div className='flex-1' />

				<Button onClick={() => navigate('/acervo/novo')}>
					Subir documento
				</Button>
			</Flex>

			{isLoading || isFetching ? (
				<div className='flex justify-center'>
					<Spinner size='md' />
				</div>
			) : (
				<Table
					th={[
						{ label: 'norma' },
						{ label: 'alcance' },
						{ label: 'dispositivos' },
						{ label: 'origem' },
						{ label: 'entrou em' },
					]}
				>
					{data?.data && data.data.length > 0 ? (
						data.data.map((document) => (
							/* A linha inteira abre a norma, como na lista de conversões. */
							<Tr
								key={document.slug}
								className='cursor-pointer hover:!bg-gray-100 transition-colors'
								onClick={() => navigate(`/acervo/${document.slug}`)}
							>
								<Td className='px-6 py-4 align-top'>
									<span className='font-medium'>{document.title}</span>
									<span className={styles.slug}>{document.slug}</span>
								</Td>

								<Td className='px-6 py-4 align-top'>
									{document.scopes.length === 0
										? 'Nacional'
										: document.scopes.join(', ')}
								</Td>

								<Td className='px-6 py-4 align-top'>
									{document.provisionCount}
									{looksTruncated(document.provisionCount, document.kind) && (
										<span className='block text-sm text-red'>
											documento pode ter entrado incompleto
										</span>
									)}
								</Td>

								<Td className='px-6 py-4 align-top'>
									<Status
										status={document.origin === 'oficial' ? 'success' : 'warn'}
										className='px-3 py-[6px] text-sm'
									>
										{document.origin === 'oficial'
											? 'Documento oficial'
											: 'Recorte do DLS'}
									</Status>
								</Td>

								<Td className='px-6 py-4'>{formatDate(document.createdAt)}</Td>
							</Tr>
						))
					) : (
						<EmptyRow
							tdAmount={5}
							message={
								applied
									? 'Nenhuma norma encontrada para essa busca'
									: 'Nenhuma norma no acervo ainda'
							}
						/>
					)}
				</Table>
			)}

			<Divider className={styles.divider} />

			{data?.data && data.data.length > 0 && (
				<Pagination
					pageCount={data.meta.lastPage || 0}
					maxAmount={data.meta.total || 0}
					perPage={data.meta.perPage || 0}
				/>
			)}
		</Template>
	)
}

export default LegalDocuments
